import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Navigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { api, extractErrorCode } from "@/lib/api";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

export default function TeamAccept() {
  const { t } = useI18n();
  const { user, loading, refresh } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [status, setStatus] = useState("loading");
  const [errorCode, setErrorCode] = useState(null);
  const [team, setTeam] = useState(null);
  // Prevent React.StrictMode (and any re-render of the effect) from firing
  // the single-use accept endpoint twice. A useRef guard scoped per token
  // is the smallest possible fix.
  const fired = useRef(false);

  useEffect(() => {
    if (loading || !user || !token) return;
    if (fired.current) return;
    fired.current = true;
    api
      .post(`/team/accept?token=${encodeURIComponent(token)}`)
      .then(async (r) => {
        setTeam(r.data.team);
        setStatus("ok");
        await refresh();
      })
      .catch((err) => {
        setErrorCode(extractErrorCode(err));
        setStatus("error");
      });
  }, [loading, user, token, refresh]);

  if (!token) return <Navigate to="/" replace />;
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#03040B] text-slate-400" data-testid="team-accept-loading">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to={`/auth?next=/team/accept?token=${encodeURIComponent(token)}`} replace />;

  // Resolve error message with proper fallback (the t() helper does not
  // support a `default` vars option, so we check the resolved key path
  // and fall back to the generic message ourselves).
  const errKey = errorCode ? `team.err_${errorCode}` : null;
  const errResolved = errKey ? t(errKey) : null;
  const errorMessage =
    errResolved && errResolved !== errKey ? errResolved : t("team.accept_error_generic");

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#03040B] px-4">
      <Card className="w-full max-w-md border-slate-800 bg-[#0A0C1F] p-8 text-center" data-testid="team-accept-card">
        {status === "loading" && (
          <>
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-[#9ACD32]" data-testid="team-accept-spinner" />
            <p className="mt-4 text-sm text-slate-300">{t("team.accept_loading")}</p>
          </>
        )}
        {status === "ok" && (
          <>
            <CheckCircle2 className="mx-auto h-10 w-10 text-[#9ACD32]" data-testid="team-accept-success-icon" />
            <h1 className="mt-4 font-display text-xl font-bold text-white" data-testid="team-accept-success-title">
              {t("team.accept_success_title")}
            </h1>
            <p className="mt-2 text-sm text-slate-400" data-testid="team-accept-success-desc">
              {t("team.accept_success_desc", { name: team?.name || "" })}
            </p>
            <Button
              onClick={() => navigate("/team")}
              className="mt-6 rounded-full bg-[#9ACD32] font-bold text-[#05073B] hover:bg-[#A8DF38]"
              data-testid="team-accept-continue-btn"
            >
              {t("team.accept_continue")}
            </Button>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle className="mx-auto h-10 w-10 text-red-500" data-testid="team-accept-error-icon" />
            <h1 className="mt-4 font-display text-xl font-bold text-white" data-testid="team-accept-error-title">
              {t("team.accept_error_title")}
            </h1>
            <p className="mt-2 text-sm text-slate-400" data-testid="team-accept-error-message">
              {errorMessage}
            </p>
            <Button
              onClick={() => navigate("/dashboard")}
              variant="outline"
              className="mt-6 rounded-full border-slate-700 text-slate-200"
              data-testid="team-accept-back-btn"
            >
              {t("team.accept_go_dashboard")}
            </Button>
          </>
        )}
      </Card>
    </div>
  );
}
